/* Iter122dx · Fase 4 · Attivazione licenze condomìni V2 (Impostazioni & Account · Le mie licenze).
 * Iter122ef · Aggiunto flusso upgrade piano (light → full/pro, full → pro) con calcolo pro-rata.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Building2, KeyRound, Landmark, CreditCard, Sparkles, Send, CheckCircle2, PauseCircle, PlayCircle, ArrowUpCircle, Check } from "lucide-react";

const STATO_MAP = {
  attiva_trial: { label: "Attivo (prova)", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  attiva:       { label: "Licenza attiva", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  richiesta:    { label: "Attivazione richiesta", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  da_attivare:  { label: "Da attivare", cls: "bg-red-100 text-red-800 border-red-300" },
  sospesa:      { label: "Sospesa", cls: "bg-slate-200 text-slate-600 border-slate-300" },
  scaduta:      { label: "Scaduta", cls: "bg-red-100 text-red-800 border-red-300" },
};
const PIANI = [
  { key: "light", label: "Light", price: "€ 79,90 /anno" },
  { key: "full",  label: "Full",  price: "€ 89,90 /anno" },
  { key: "pro",   label: "Pro+",  price: "€ 99,90 /anno" },
];
const PIANO_ORDER = { light: 1, full: 2, pro: 3 };

export default function LicenzeCondominiV2() {
  const [list, setList] = useState([]);
  const [bonifico, setBonifico] = useState(null);
  const [attivaTarget, setAttivaTarget] = useState(null); // condominio
  const [form, setForm] = useState({ piano: "light", intestatario: "studio", metodo_pagamento: "bonifico" });
  const [busy, setBusy] = useState(false);
  // Iter122ef · state upgrade dialog
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [upgradeForm, setUpgradeForm] = useState({ piano_target: "full", metodo_pagamento: "bonifico" });
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [upgradeBusy, setUpgradeBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: cs }, { data: b }] = await Promise.all([
        api.get("/me/condomini-licenze"),
        api.get("/piani/info"),
      ]);
      setList(cs); setBonifico(b?.bonifico || null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento"); }
  };
  useEffect(() => { load(); }, []);

  // Iter122ez · Deep-link: se arrivo da Condomini con ?attiva={cid} apro il modal attivazione
  useEffect(() => {
    if (!list.length) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const cid = params.get("attiva");
      if (cid) {
        const c = list.find((x) => x.id === cid);
        if (c) openAttiva(c);
      }
    } catch { /* URLSearchParams unsupported */ }
  }, [list.length]);

  const openAttiva = (c) => {
    setAttivaTarget(c);
    setForm({ piano: c.licenza_piano || "light", intestatario: c.licenza_intestatario || "studio", metodo_pagamento: c.licenza_metodo_pagamento || "bonifico" });
  };

  const invia = async () => {
    if (!attivaTarget) return;
    setBusy(true);
    try {
      await api.post(`/me/condomini/${attivaTarget.id}/richiedi-attivazione`, form);
      toast.success("Richiesta inviata — lo Studio verificherà il pagamento e attiverà la licenza.");
      setAttivaTarget(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
    setBusy(false);
  };

  // Iter122ef · Apri dialog upgrade: precompila piano target al primo livello superiore e carica preview
  const openUpgrade = async (c) => {
    const pcurr = (c.licenza_piano || "light").toLowerCase();
    const suggerito = pcurr === "light" ? "full" : "pro";
    setUpgradeTarget(c);
    setUpgradeForm({ piano_target: suggerito, metodo_pagamento: "bonifico" });
    setUpgradePreview(null);
    try {
      const { data } = await api.get(`/me/condomini/${c.id}/upgrade-preview`, { params: { piano_target: suggerito } });
      setUpgradePreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Anteprima non disponibile"); }
  };

  const onUpgradePianoChange = async (piano_target) => {
    setUpgradeForm({ ...upgradeForm, piano_target });
    setUpgradePreview(null);
    try {
      const { data } = await api.get(`/me/condomini/${upgradeTarget.id}/upgrade-preview`, { params: { piano_target } });
      setUpgradePreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Anteprima non disponibile"); }
  };

  const inviaUpgrade = async () => {
    if (!upgradeTarget) return;
    setUpgradeBusy(true);
    try {
      const { data } = await api.post(`/me/condomini/${upgradeTarget.id}/richiedi-upgrade`, upgradeForm);
      // Iter122eg · Stripe: redirect immediato al checkout, se disponibile
      if (upgradeForm.metodo_pagamento === "stripe" && data.stripe_url) {
        toast.success("Redirect al pagamento Stripe…");
        window.location.assign(data.stripe_url);
        return;
      }
      toast.success("Richiesta di upgrade inviata — lo Studio attiverà il piano dopo la verifica del pagamento.");
      setUpgradeTarget(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
    setUpgradeBusy(false);
  };

  const causale = attivaTarget && bonifico?.causale_template
    ? bonifico.causale_template.replace("{nome_condominio}", attivaTarget.nome || "")
    : "";

  return (
    <div className="space-y-3" data-testid="licenze-condomini-v2">
      {list.length === 0 && <p className="text-sm text-slate-500 italic">Non hai ancora creato palazzine. Crea la prima in Stabili.</p>}
      {list.map((c) => {
        const st = STATO_MAP[c.licenza_stato] || STATO_MAP.da_attivare;
        // Iter122ej · Il condominio in prova (`attiva_trial`) mostra "Attiva Condominio"
        // (conversione trial → licenza pagata), NON "Fai upgrade" (che è solo tra piani pagati).
        const showAttiva = ["da_attivare", "sospesa", "scaduta", "attiva_trial"].includes(c.licenza_stato);
        return (
          <Card key={c.id} className="p-3 border-2" data-testid={`licenza-card-${c.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-2 sm:gap-3">
              <div className="flex items-start sm:items-center gap-3 w-full sm:w-auto sm:flex-1 sm:min-w-0">
                <Building2 className="w-5 h-5 text-brand shrink-0 mt-0.5 sm:mt-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-bold break-words">{c.nome}</div>
                  <div className="text-xs text-slate-500 break-words">{c.indirizzo}, {c.citta}</div>
                </div>
              </div>
              <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto">
                <span className={`px-2 py-0.5 rounded-full border text-xs font-bold ${st.cls}`} data-testid={`licenza-badge-${c.id}`}>{st.label}</span>
                {c.licenza_piano && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">Piano: {c.licenza_piano.toUpperCase()}</span>}
                {c.licenza_scadenza && <span className="text-xs text-slate-500">Scad: {new Date(c.licenza_scadenza).toLocaleDateString("it-IT")}</span>}
                {showAttiva && (
                  <Button size="sm" className="h-8 bg-brand hover:bg-brand-dark"
                    onClick={() => openAttiva(c)} data-testid={`attiva-cond-btn-${c.id}`}>
                    <KeyRound className="w-4 h-4 mr-1" /> Attiva Condominio
                  </Button>
                )}
                {/* Iter122ef · Upgrade piano visibile solo se GIÀ pagato (attiva) E non ancora Pro+ · Iter122ej · escluso attiva_trial */}
                {c.licenza_stato === "attiva"
                  && (PIANO_ORDER[(c.licenza_piano || "light").toLowerCase()] || 1) < 3
                  && !(c.licenza_upgrade && c.licenza_upgrade.stato === "richiesto") && (
                  <Button size="sm" variant="outline" className="h-8 border-amber-400 text-amber-800 hover:bg-amber-50"
                    onClick={() => openUpgrade(c)} data-testid={`upgrade-cond-btn-${c.id}`}>
                    <ArrowUpCircle className="w-4 h-4 mr-1" /> Fai upgrade
                  </Button>
                )}
                {c.licenza_upgrade && c.licenza_upgrade.stato === "richiesto" && (
                  <span className="px-2 py-0.5 rounded-full border text-xs font-bold bg-amber-100 text-amber-800 border-amber-300"
                    data-testid={`upgrade-badge-${c.id}`}>
                    Upgrade richiesto → {c.licenza_upgrade.piano_target?.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </Card>
        );
      })}

      {/* Dialog attivazione */}
      <Dialog open={!!attivaTarget} onOpenChange={(o) => !o && setAttivaTarget(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="attiva-cond-dialog">
          <DialogHeader><DialogTitle className="text-xl">Attiva licenza · {attivaTarget?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {/* Piano */}
            <div>
              <Label className="text-sm font-bold">Piano</Label>
              <div className="grid sm:grid-cols-3 gap-2 mt-1">
                {PIANI.map((p) => (
                  <button key={p.key} type="button" data-testid={`piano-${p.key}`}
                    onClick={() => setForm({ ...form, piano: p.key })}
                    className={`p-2 rounded-lg border-2 text-left ${form.piano === p.key ? "border-brand bg-brand/5" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="font-bold text-sm">{p.label}</div>
                    <div className="text-xs text-slate-500">{p.price}</div>
                  </button>
                ))}
              </div>
            </div>
            {/* Intestatario fattura */}
            <div>
              <Label className="text-sm font-bold">Intestatario fattura</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-1">
                <button type="button" data-testid="intestatario-studio"
                  onClick={() => setForm({ ...form, intestatario: "studio" })}
                  className={`p-2 rounded-lg border-2 text-left ${form.intestatario === "studio" ? "border-brand bg-brand/5" : "border-slate-200"}`}>
                  <div className="font-bold text-sm">Amministratore p.t. (Studio)</div>
                  <div className="text-xs text-slate-500">Se paghi tu con carta o dal tuo conto</div>
                </button>
                <button type="button" data-testid="intestatario-condominio"
                  onClick={() => setForm({ ...form, intestatario: "condominio" })}
                  className={`p-2 rounded-lg border-2 text-left ${form.intestatario === "condominio" ? "border-brand bg-brand/5" : "border-slate-200"}`}>
                  <div className="font-bold text-sm">Condominio</div>
                  <div className="text-xs text-slate-500">Se paga il condominio dal proprio conto</div>
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">I dati fatturazione vengono presi automaticamente dalla scheda Stabili (sezione {form.intestatario === "studio" ? "Studio · Profilo" : "dati fatturazione condominio"}).</p>
            </div>
            {/* Metodo pagamento */}
            <div>
              <Label className="text-sm font-bold">Modalità di pagamento</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-1">
                <button type="button" data-testid="metodo-bonifico"
                  onClick={() => setForm({ ...form, metodo_pagamento: "bonifico" })}
                  className={`p-2 rounded-lg border-2 text-left ${form.metodo_pagamento === "bonifico" ? "border-brand bg-brand/5" : "border-slate-200"}`}>
                  <div className="font-bold text-sm inline-flex items-center gap-1"><Landmark className="w-4 h-4" /> Bonifico bancario</div>
                  <div className="text-xs text-slate-500">Attivazione entro ~48h dal pagamento</div>
                </button>
                <button type="button" data-testid="metodo-stripe"
                  onClick={() => setForm({ ...form, metodo_pagamento: "stripe" })}
                  className={`p-2 rounded-lg border-2 text-left ${form.metodo_pagamento === "stripe" ? "border-brand bg-brand/5" : "border-slate-200"}`}>
                  <div className="font-bold text-sm inline-flex items-center gap-1"><CreditCard className="w-4 h-4" /> Pagamento elettronico (Stripe)</div>
                  <div className="text-xs text-slate-500">Carta di credito · attivazione immediata</div>
                </button>
              </div>
            </div>
            {/* Box bonifico */}
            {form.metodo_pagamento === "bonifico" && bonifico && (
              <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-3 text-sm" data-testid="bonifico-dettagli">
                <div className="font-bold text-amber-900 mb-1 inline-flex items-center gap-1"><Landmark className="w-4 h-4" /> Estremi bonifico</div>
                <dl className="space-y-0.5 text-slate-700">
                  <div><b>Intestatario:</b> {bonifico.intestatario || "—"}</div>
                  <div><b>IBAN:</b> <span className="font-mono">{bonifico.iban || "—"}</span></div>
                  {bonifico.banca && <div><b>Banca:</b> {bonifico.banca}</div>}
                  <div className="pt-1"><b>Causale:</b> <span className="italic">{causale}</span></div>
                </dl>
                <p className="text-xs text-amber-800 mt-2">{bonifico.note}</p>
                {bonifico.email_contabile && <p className="text-xs text-amber-800 mt-1">Contabile a: <a href={`mailto:${bonifico.email_contabile}`} className="underline font-semibold">{bonifico.email_contabile}</a></p>}
              </div>
            )}
            {form.metodo_pagamento === "stripe" && (
              <div className="rounded-lg border-2 border-sky-200 bg-sky-50 p-3 text-sm text-sky-900" data-testid="stripe-dettagli">
                <div className="font-bold mb-1 inline-flex items-center gap-1"><CreditCard className="w-4 h-4" /> Pagamento elettronico</div>
                <p>Alla conferma della richiesta lo Studio ti invierà il link Stripe per pagare con carta.
                  L&apos;attivazione della licenza è immediata al completamento del pagamento.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setAttivaTarget(null)}>Annulla</Button>
            <Button className="bg-brand hover:bg-brand-dark" disabled={busy} onClick={invia} data-testid="attiva-cond-invia">
              <Send className="w-4 h-4 mr-1" /> {busy ? "Invio…" : "Invia richiesta"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter122ef · Dialog upgrade piano condominio */}
      <Dialog open={!!upgradeTarget} onOpenChange={(o) => !o && setUpgradeTarget(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="upgrade-cond-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl inline-flex items-center gap-2">
              <ArrowUpCircle className="w-5 h-5 text-amber-600" />
              Upgrade licenza · {upgradeTarget?.nome}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Piano attuale: <b>{(upgradeTarget?.licenza_piano || "light").toUpperCase()}</b> —
              scadenza <b>{upgradeTarget?.licenza_scadenza && new Date(upgradeTarget.licenza_scadenza).toLocaleDateString("it-IT")}</b>.
              L&apos;upgrade non modifica la data di scadenza; paghi solo la differenza pro-rata sul residuo.
            </p>
            {/* Piano target: solo i piani superiori all'attuale */}
            <div>
              <Label className="text-sm font-bold">Piano di destinazione</Label>
              <div className="grid sm:grid-cols-3 gap-2 mt-1">
                {PIANI.filter((p) => (PIANO_ORDER[p.key] > (PIANO_ORDER[(upgradeTarget?.licenza_piano || "light").toLowerCase()] || 1))).map((p) => (
                  <button key={p.key} type="button" data-testid={`upgrade-piano-${p.key}`}
                    onClick={() => onUpgradePianoChange(p.key)}
                    className={`p-2 rounded-lg border-2 text-left ${upgradeForm.piano_target === p.key ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="font-bold text-sm">{p.label}</div>
                    <div className="text-xs text-slate-500">{p.price}</div>
                  </button>
                ))}
              </div>
            </div>
            {/* Preview differenza pro-rata + sezioni sbloccate */}
            {upgradePreview && (
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3" data-testid="upgrade-preview">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-emerald-800 uppercase font-bold tracking-wide">Da pagare oggi</div>
                    <div className="text-2xl font-bold text-emerald-900 mt-0.5">€ {upgradePreview.delta_lordo.toFixed(2)}</div>
                    <div className="text-xs text-emerald-700">
                      IVA 22% inclusa · residuo {upgradePreview.residuo_giorni}gg su 365
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      (netto € {upgradePreview.delta_netto.toFixed(2)} + IVA € {upgradePreview.delta_iva.toFixed(2)})
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-emerald-800 uppercase font-bold tracking-wide">Sezioni che verranno sbloccate</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {upgradePreview.sezioni_sbloccate.length === 0 && (
                        <span className="text-xs text-slate-500 italic">Nessuna sezione aggiuntiva</span>
                      )}
                      {upgradePreview.sezioni_sbloccate.map((s) => (
                        <span key={s.key} className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-300 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">
                          <Check className="w-3 h-3" /> {s.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Metodo pagamento */}
            <div>
              <Label className="text-sm font-bold">Modalità di pagamento</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-1">
                <button type="button" data-testid="upgrade-metodo-bonifico"
                  onClick={() => setUpgradeForm({ ...upgradeForm, metodo_pagamento: "bonifico" })}
                  className={`p-2 rounded-lg border-2 text-left ${upgradeForm.metodo_pagamento === "bonifico" ? "border-brand bg-brand/5" : "border-slate-200"}`}>
                  <div className="font-bold text-sm inline-flex items-center gap-1"><Landmark className="w-4 h-4" /> Bonifico bancario</div>
                  <div className="text-xs text-slate-500">Attivazione dopo verifica dello Studio</div>
                </button>
                <button type="button" data-testid="upgrade-metodo-stripe"
                  onClick={() => setUpgradeForm({ ...upgradeForm, metodo_pagamento: "stripe" })}
                  className={`p-2 rounded-lg border-2 text-left ${upgradeForm.metodo_pagamento === "stripe" ? "border-brand bg-brand/5" : "border-slate-200"}`}>
                  <div className="font-bold text-sm inline-flex items-center gap-1"><CreditCard className="w-4 h-4" /> Pagamento elettronico (Stripe)</div>
                  <div className="text-xs text-slate-500">Carta di credito · attivazione immediata</div>
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setUpgradeTarget(null)}>Annulla</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" disabled={upgradeBusy || !upgradePreview}
              onClick={inviaUpgrade} data-testid="upgrade-cond-invia">
              <ArrowUpCircle className="w-4 h-4 mr-1" /> {upgradeBusy ? "Invio…" : "Conferma upgrade"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
